/**
 * Knowledge Graph Design API Service
 *
 * Handles all API calls for the Knowledge Graph Design workflow:
 * - Phase 1: Schema Exploration (table browsing, AI suggestions, selection)
 * - Phase 2: Relationship Discovery (FK-based, AI-inferred, approval)
 * - Phase 3: Schema Preview (Mermaid diagram, coverage, GDS recommendations)
 */

import axios from 'axios';
import apiClient from '../api';
import type {
  ModuleWithTables,
  TableColumn,
  SuggestTablesResponse,
  SelectTablesResponse,
  DeselectTablesResponse,
  TableSuggestion,
  RelationshipDiscoveryResult,
  DiscoveredRelationship,
  BulkApproveResponse,
  SchemaPreview,
  SchemaVisualizationResponse,
  CoverageAnalysis,
  AnswerableQuestionsResponse,
  GDSRecommendation,
  ExpansionPreviewResponse,
  DigitalTwinRecommendationRequest,
  DigitalTwinTableRecommendation,
  CompleteSchemaResponse,
  CompletedSchema,
} from '../../types/knowledgeGraphDesign';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://graph-solution-backend.huhoka.com/api';

// Create dedicated axios instance for knowledge graph design
// Uses /case-analysis/cases as the base path to match backend routes
const kgdClient = axios.create({
  baseURL: `${API_BASE_URL}/case-analysis/cases`,
  timeout: 60000, // 1 minute default
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
kgdClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for AI-powered endpoints
    if (config.url?.includes('/suggest') || config.url?.includes('/discover') || config.url?.includes('/preview')) {
      config.timeout = 180000; // 3 minutes for AI operations
    }

    // Quick schema generation can take 5-10 minutes with local LLM (Ollama qwen2:latest)
    if (config.url?.includes('/quick-generate')) {
      config.timeout = 600000; // 10 minutes for AI schema generation with Ollama
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Phase 1: Schema Exploration API
// ============================================================================

/**
 * Get all modules for an enterprise system
 */
export const getModulesForSystem = async (
  caseId: string,
  systemId: string
): Promise<ModuleWithTables[]> => {
  // include_tables=true: without this the backend returns `table_count` instead
  // of a populated `tables[]` array, so the SchemaExplorationPanel header reads
  // "N modules, 0 tables" and the browse view has nothing to show.
  const response = await kgdClient.get(`/${caseId}/systems/${systemId}/modules?include_tables=true`);
  return response.data;
};

/**
 * Get tables for a specific module
 * Note: systemId is kept for API consistency but not used in the path
 */
export const getTablesForModule = async (
  caseId: string,
  _systemId: string,
  moduleId: string
): Promise<ModuleWithTables> => {
  const response = await kgdClient.get(`/${caseId}/modules/${moduleId}/tables`);
  return response.data;
};

/**
 * Get columns for a specific table
 */
export const getColumnsForTable = async (
  caseId: string,
  _systemId: string,
  tableId: string
): Promise<TableColumn[]> => {
  const response = await kgdClient.get(`/${caseId}/tables/${tableId}/columns`);
  return response.data;
};

/**
 * Get AI-powered table suggestions based on strategic questions
 */
export const suggestTablesForQuestions = async (
  caseId: string,
  _systemId: string,
  questionIds?: string[],
  maxSuggestions: number = 20
): Promise<SuggestTablesResponse> => {
  const response = await kgdClient.post(`/${caseId}/tables/suggest`, {
    question_ids: questionIds,
    max_suggestions: maxSuggestions,
  });
  return response.data;
};

/**
 * Select tables for a case
 */
export const selectTablesForCase = async (
  caseId: string,
  _systemId: string,
  tableIds: string[]
): Promise<SelectTablesResponse> => {
  const response = await kgdClient.post(`/${caseId}/tables/select`, {
    table_ids: tableIds,
  });
  return response.data;
};

/**
 * Deselect tables from a case
 */
export const deselectTablesFromCase = async (
  caseId: string,
  _systemId: string,
  tableIds: string[]
): Promise<DeselectTablesResponse> => {
  const response = await kgdClient.post(`/${caseId}/tables/deselect`, {
    table_ids: tableIds,
  });
  return response.data;
};

/**
 * Get tables already selected for a case
 * Returns tables that were previously selected via AI Digital Twin or manual selection
 */
export interface SelectedTableInfo {
  table_id: string;
  table_name: string;
  module_name?: string;
  system_id?: string;
  system_name?: string;
  selection_reason?: string;
  selected_at?: string;
  column_count?: number;
}

export const getSelectedTablesForCase = async (
  caseId: string
): Promise<SelectedTableInfo[]> => {
  const response = await kgdClient.get(`/${caseId}/tables/selected`);
  return response.data;
};

/**
 * Get current table suggestions for a case
 */
export const getTableSuggestions = async (
  caseId: string,
  _systemId: string
): Promise<TableSuggestion[]> => {
  const response = await kgdClient.get(`/${caseId}/suggestions`);
  return response.data;
};

/**
 * Update suggestion status (accept/reject)
 */
export const updateSuggestionStatus = async (
  caseId: string,
  _systemId: string,
  suggestionId: string,
  status: 'accepted' | 'rejected'
): Promise<{ success: boolean; message: string }> => {
  const response = await kgdClient.patch(
    `/${caseId}/suggestions/${suggestionId}/status`,
    { status }
  );
  return response.data;
};

// ============================================================================
// Phase 2: Relationship Discovery API
// ============================================================================

/**
 * Discover relationships between selected tables
 * Note: systemId is kept for API consistency but not used in the path
 */
export const discoverRelationships = async (
  caseId: string,
  _systemId: string,
  includeAiInference: boolean = true,
  confidenceThreshold: number = 0.6
): Promise<RelationshipDiscoveryResult> => {
  const response = await kgdClient.post(`/${caseId}/relationships/discover`, {
    include_ai_inference: includeAiInference,
    confidence_threshold: confidenceThreshold,
  });
  return response.data;
};

/**
 * Get all discovered relationships
 */
export const getDiscoveredRelationships = async (
  caseId: string,
  _systemId?: string
): Promise<DiscoveredRelationship[]> => {
  const response = await kgdClient.get(`/${caseId}/relationships`);
  return response.data;
};

/**
 * Get pending relationships (awaiting approval)
 */
export const getPendingRelationships = async (
  caseId: string,
  _systemId?: string
): Promise<DiscoveredRelationship[]> => {
  const response = await kgdClient.get(`/${caseId}/relationships/pending`);
  return response.data;
};

/**
 * Approve a relationship
 */
export const approveRelationship = async (
  caseId: string,
  _systemId: string,
  relationshipId: string,
  approvedBy: string,
  notes?: string
): Promise<DiscoveredRelationship> => {
  const response = await kgdClient.put(
    `/${caseId}/relationships/${relationshipId}/approve`,
    { approved_by: approvedBy, notes }
  );
  return response.data;
};

/**
 * Reject a relationship
 */
export const rejectRelationship = async (
  caseId: string,
  _systemId: string,
  relationshipId: string,
  rejectedBy: string,
  reason: string
): Promise<DiscoveredRelationship> => {
  const response = await kgdClient.put(
    `/${caseId}/relationships/${relationshipId}/reject`,
    { rejected_by: rejectedBy, reason }
  );
  return response.data;
};

/**
 * Bulk approve relationships
 */
export const bulkApproveRelationships = async (
  caseId: string,
  _systemId: string,
  relationshipIds: string[],
  approvedBy: string
): Promise<BulkApproveResponse> => {
  const response = await kgdClient.post(
    `/${caseId}/relationships/bulk-approve`,
    { relationship_ids: relationshipIds, approved_by: approvedBy }
  );
  return response.data;
};

// ============================================================================
// Phase 3: Schema Preview API
// ============================================================================

/**
 * AI-based schema generation response type
 */
export interface AIGeneratedSchema {
  nodes: Array<{
    label: string;
    properties: Array<{
      name: string;
      type: string;
      required?: boolean;
      unique?: boolean;
      indexed?: boolean;
      description?: string;
    }>;
    description?: string;
    sourceTables?: string[];
  }>;
  relationships: Array<{
    type: string;
    from: string;
    to: string;
    cardinality?: string;
    properties?: Array<{
      name: string;
      type: string;
      required?: boolean;
      description?: string;
    }>;
    description?: string;
  }>;
  constraints: Array<{
    name: string;
    label: string;
    properties: string[];
    type: string;
    description?: string;
  }>;
  indexes: Array<{
    name: string;
    label: string;
    properties: string[];
    type: string;
    description?: string;
  }>;
  cypherDDL: string;
  metadata?: {
    generation_time_seconds?: number;
    ai_model?: string;
    llm_provider?: string;
    tables_analyzed?: number;
    total_tokens?: number;
    generated_at?: string;
  };
}

/**
 * Table column definition for quick schema generation
 */
export interface QuickSchemaTableColumn {
  column_name: string;
  data_type?: string;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  foreign_key_reference?: string;
  description?: string;
  business_meaning?: string;
}

/**
 * Table definition for quick schema generation
 */
export interface QuickSchemaTable {
  table_id: string;
  table_name: string;
  module_name?: string;
  system_name?: string;
  description?: string;
  columns: QuickSchemaTableColumn[];
  primary_keys?: string[];
}

/**
 * Request body for quick schema generation
 */
export interface QuickSchemaGenerationRequest {
  tables: QuickSchemaTable[];
  system_id?: string;  // If provided, backend will auto-fetch columns from Neo4j
  selected_tables?: string[];  // Table names to fetch if system_id is provided
  strategic_questions?: string[];
  business_context?: string;
  include_gds_recommendations?: boolean;
}

/**
 * Response from quick schema generation
 */
export interface QuickSchemaGenerationResponse {
  schema_id: string;
  nodes: AIGeneratedSchema['nodes'];
  relationships: AIGeneratedSchema['relationships'];
  constraints: AIGeneratedSchema['constraints'];
  indexes: AIGeneratedSchema['indexes'];
  cypherDDL: string;
  metadata: {
    tables_analyzed: number;
    generation_time_seconds: number;
    llm_provider: string;
    total_tokens: number;
    generated_at: string;
  };
}

/**
 * Generate Neo4j schema using AI from enterprise system tables
 * This calls the backend AI to analyze tables and create meaningful schema
 */
export const generateAISchema = async (
  caseId: string,
  systemId?: string,
  options?: {
    includeRelationships?: boolean;
    maxNodes?: number;
    optimizeForQueries?: boolean;
  }
): Promise<AIGeneratedSchema> => {
  const requestBody: Record<string, any> = {
    include_relationships: options?.includeRelationships ?? true,
    max_nodes: options?.maxNodes ?? 50,
    optimize_for_queries: options?.optimizeForQueries ?? true,
  };

  if (systemId) {
    requestBody.system_id = systemId;
  }

  const response = await kgdClient.post(`/${caseId}/schema/generate`, requestBody);
  return response.data;
};

/**
 * Quick schema generation from tables using AI
 * This bypasses the data mapping step and generates schema directly from table definitions
 */
export const generateQuickSchema = async (
  caseId: string,
  request: QuickSchemaGenerationRequest
): Promise<QuickSchemaGenerationResponse> => {
  const response = await kgdClient.post(`/${caseId}/schema/quick-generate`, request, {
    timeout: 600000, // 10 minutes for AI generation with Ollama
  });
  return response.data;
};

/**
 * Response from saved schema fetch
 */
export interface SavedSchemaResponse {
  caseId: string;
  version: number;
  isActive: boolean;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  schema?: {
    nodes: AIGeneratedSchema['nodes'];
    relationships: AIGeneratedSchema['relationships'];
    constraints: AIGeneratedSchema['constraints'];
    indexes: AIGeneratedSchema['indexes'];
    cypherDDL?: string;
    metadata?: {
      source: string;
      schema_id?: string;
      saved_at?: string;
    };
  };
  createdAt?: string;
  completedAt?: string;
  errorMessage?: string;
}

/**
 * Get saved schema design from Neo4j
 * Returns null if no schema exists for the case
 */
export const getSavedSchema = async (
  caseId: string
): Promise<SavedSchemaResponse | null> => {
  try {
    // Use schema-design endpoint which fetches from Neo4j
    const response = await axios.get(`${API_BASE_URL}/schema-design/cases/${caseId}/schema`);
    return response.data;
  } catch (error: any) {
    // 404 means no schema exists yet - not an error
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Save quick-generated schema to Neo4j
 */
export const saveQuickSchema = async (
  caseId: string,
  schema: QuickSchemaGenerationResponse
): Promise<{
  success: boolean;
  message: string;
  caseId: string;
  version: number;
  status: string;
  createdAt?: string;
  completedAt?: string;
}> => {
  const response = await kgdClient.post(`/${caseId}/schema/save`, {
    schema_id: schema.schema_id,
    nodes: schema.nodes,
    relationships: schema.relationships,
    constraints: schema.constraints,
    indexes: schema.indexes,
    cypherDDL: schema.cypherDDL,
  });
  return response.data;
};

/**
 * Generate complete schema preview
 */
export const generateSchemaPreview = async (
  caseId: string,
  systemId?: string,
  includeGdsRecommendations: boolean = true,
  includeCoverageAnalysis: boolean = true
): Promise<SchemaPreview> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);
  if (includeGdsRecommendations) params.append('include_gds_recommendations', 'true');
  if (includeCoverageAnalysis) params.append('include_coverage_analysis', 'true');

  const response = await kgdClient.get(`/${caseId}/schema/preview?${params.toString()}`);
  return response.data;
};

/**
 * Get schema visualization (Mermaid diagram)
 */
export const getSchemaVisualization = async (
  caseId: string,
  systemId?: string,
  format: 'mermaid' | 'json' = 'mermaid'
): Promise<SchemaVisualizationResponse> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);
  params.append('format', format);

  const response = await kgdClient.get(`/${caseId}/schema/visualization?${params.toString()}`);
  return response.data;
};

/**
 * Get coverage analysis for strategic questions
 */
export const getCoverageAnalysis = async (
  caseId: string,
  systemId?: string
): Promise<CoverageAnalysis> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);

  const response = await kgdClient.get(`/${caseId}/schema/coverage-analysis?${params.toString()}`);
  return response.data;
};

/**
 * Get which questions can be answered with current schema
 */
export const getAnswerableQuestions = async (
  caseId: string,
  systemId?: string
): Promise<AnswerableQuestionsResponse> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);

  const response = await kgdClient.get(`/${caseId}/schema/answerable-questions?${params.toString()}`);
  return response.data;
};

/**
 * Get GDS algorithm recommendations
 */
export const getGDSRecommendations = async (
  caseId: string,
  systemId?: string
): Promise<GDSRecommendation[]> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);

  const response = await kgdClient.get(`/${caseId}/schema/gds-recommendations?${params.toString()}`);
  return response.data;
};

/**
 * Preview schema expansion impact
 */
export const getExpansionPreview = async (
  caseId: string,
  systemId?: string,
  tableIds?: string[]
): Promise<ExpansionPreviewResponse> => {
  const params = new URLSearchParams();
  if (systemId) params.append('system_id', systemId);
  if (tableIds && tableIds.length > 0) {
    tableIds.forEach(id => params.append('table_ids', id));
  }

  const response = await kgdClient.get(`/${caseId}/schema/expansion-preview?${params.toString()}`);
  return response.data;
};

// ============================================================================
// Digital Twin Table Recommendations API
// ============================================================================

/**
 * Get AI-powered Digital Twin table recommendations
 * Analyzes strategic questions and enterprise system schemas to recommend
 * Must-Have, Should-Have, and Could-Have tables for creating a digital twin.
 *
 * This operation can take several minutes as it uses LLM analysis.
 */
export const getDigitalTwinRecommendations = async (
  caseId: string,
  request: DigitalTwinRecommendationRequest
): Promise<DigitalTwinTableRecommendation> => {
  const response = await kgdClient.post(
    `/${caseId}/digital-twin/recommend-tables`,
    request,
    {
      timeout: 600000, // 10 minutes for AI analysis with Ollama
    }
  );
  return response.data;
};

/**
 * Get selected enterprise systems for a case
 * Used to populate the system selection for digital twin recommendations
 */
export const getSelectedSystemsForCase = async (
  caseId: string
): Promise<{
  case_id: string;
  selected_systems: Array<{
    system_id: string;
    system_name: string;
    vendor: string;
    category: string;
    module_count?: number;
    table_count?: number;
  }>;
  total_systems: number;
}> => {
  const response = await kgdClient.get(`/${caseId}/digital-twin/selected-systems`);
  return response.data;
};

// ============================================================================
// Enterprise Systems Library API
// ============================================================================

/**
 * Available enterprise system from the library
 */
export interface AvailableEnterpriseSystem {
  system_id: string;
  system_name: string;
  vendor: string;
  category: string;
  version?: string;
  description?: string;
  quality_score?: number;
  quality_grade?: string;
  total_modules?: number;
  total_tables?: number;
  status?: string;
}

/**
 * Get all available enterprise systems from the research library
 * These are the systems that can be selected for a case
 */
export const getAvailableEnterpriseSystems = async (
  options?: {
    category?: string;
    page?: number;
    pageSize?: number;
  }
): Promise<{
  systems: AvailableEnterpriseSystem[];
  total: number;
}> => {
  const params = new URLSearchParams();
  if (options?.category) params.append('category', options.category);
  if (options?.page) params.append('page', String(options.page));
  if (options?.pageSize) params.append('page_size', String(options.pageSize));

  // Use direct API URL since this is not under /case-analysis/cases
  const response = await axios.get(
    `${API_BASE_URL}/enterprise-systems?${params.toString()}`
  );
  return response.data;
};

/**
 * Smart-list enterprise systems for a specific industry, with LLM-backed
 * generation as a fallback.  If Neo4j already has EnterpriseSystem nodes whose
 * `target_industries` cover this industry, those are returned instantly.
 * Otherwise the backend calls the LLM (~30s) to generate 8-12 industry-relevant
 * systems, persists them, and returns the new set.
 *
 * Pass `forceRegenerate=true` to skip the cache and re-run the LLM.
 */
export const getEnterpriseSystemsByIndustry = async (
  industry: string,
  options?: {
    forceRegenerate?: boolean;
    targetCount?: number;
  }
): Promise<{
  systems: AvailableEnterpriseSystem[];
  source: 'existing' | 'generated';
  industry: string;
  count: number;
}> => {
  const response = await axios.post(
    `${API_BASE_URL}/enterprise-systems/by-industry`,
    {
      industry,
      force_regenerate: options?.forceRegenerate ?? false,
      target_count: options?.targetCount ?? 10,
    },
    // 180s — must stay ABOVE the backend's server-side generation deadline
    // (enterprise_systems_generation_timeout_seconds, default 170s) so the
    // backend's result OR its bounded 504 always arrives before axios aborts
    // (the old hang was an UNBOUNDED backend call). The persist subtree is now
    // batched (a few round-trips, not ~160). Cache-hit returns are sub-second,
    // and the Step-2 picker no longer blocks on this call regardless.
    { timeout: 180000 }
  );
  return response.data;
};

/**
 * One signal in the server-side system recommendation breakdown (same shape
 * the Step 0 domain/hypothesis recommender uses).
 */
export interface SystemRecommendationSignal {
  signal: string;
  label: string;
  value: number;
  weight: number;
  contribution: number;
  explanation: string;
}

/** Per-system result of `recommendSystemsForCase`. */
export interface SystemRecommendation {
  system_id: string;
  system_name: string;
  category: string;
  recommendation_score: number;
  /** One sentence naming the shared data areas and the questions served. */
  recommendation_reason: string;
  recommendation_signals: SystemRecommendationSignal[];
  /** Question ids whose KG areas intersect the system's areas. */
  serves_questions: string[];
  kg_areas: string[];
  shared_areas: string[];
}

/** The one pick per category. `margin` is null for a single-system category. */
export interface SystemRecommendationPick {
  system_id: string;
  system_name: string;
  confidence: 'strong' | 'marginal';
  score: number;
  margin: number | null;
  system_count: number;
}

export interface SystemsRecommendationResponse {
  case_id: string;
  industry: string | null;
  case_areas: string[];
  question_count: number;
  hypothesis_count: number;
  systems: SystemRecommendation[];
  recommended_by_category: Record<string, SystemRecommendationPick>;
}

/**
 * Score enterprise systems AGAINST A CASE, server-side: each system's tables
 * and modules are mapped to KG areas and overlapped with the areas the case's
 * strategic questions need. Pure graph read (no LLM), so it is fast.
 *
 * `systemIds` defaults (server-side) to the systems tagged with the case's
 * industry plus the published library; pass the ids the picker is showing so
 * the chips line up with the visible cards. Uses the shared client so the
 * request carries the Authorization header.
 */
export const recommendSystemsForCase = async (
  caseId: string,
  systemIds?: string[]
): Promise<SystemsRecommendationResponse> => {
  const response = await apiClient.post<SystemsRecommendationResponse>(
    '/enterprise-systems/recommend-for-case',
    {
      case_id: caseId,
      ...(systemIds ? { system_ids: systemIds } : {}),
    }
  );
  return response.data;
};

// ============================================================================
// Schema Completion API (Analysis → Design Phase Transition)
// ============================================================================

/**
 * Complete the schema design and transition to Design phase
 * This saves the schema preview data and marks the Analysis phase as complete
 *
 * @param caseId - The case ID
 * @param schemaData - The full schema preview data from generateSchemaPreview()
 * @param notes - Optional notes about the completion
 * @returns CompleteSchemaResponse with schema_id and completion status
 */
export const completeSchemaDesign = async (
  caseId: string,
  schemaData: SchemaPreview,
  notes?: string
): Promise<CompleteSchemaResponse> => {
  const response = await kgdClient.post(`/${caseId}/schema/complete`, {
    schema_data: schemaData,
    notes,
  });
  return response.data;
};

/**
 * Get the completed schema for a case
 * Returns the saved schema design that was completed via completeSchemaDesign()
 *
 * @param caseId - The case ID
 * @returns CompletedSchema with full schema data, or null if not completed
 */
export const getCompletedSchema = async (
  caseId: string
): Promise<CompletedSchema | null> => {
  try {
    const response = await kgdClient.get(`/${caseId}/schema/completed`);
    return response.data;
  } catch (error: any) {
    // 404 means no completed schema exists yet - not an error
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// ============================================================================
// KG-First Schema Generation (Advanced Mode - Concept-First Approach)
// ============================================================================

/**
 * Conceptual node in KG-first schema (not 1:1 table mapping)
 */
export interface KGFirstConcept {
  concept_id: string;
  concept_name: string;
  kg_area_id: string;
  kg_area_name: string;
  description: string;
  source_tables: Array<{
    table_name: string;
    table_id: string | null;
  }>;
  properties: Array<{
    name: string;
    type: string;
    from_table?: string;
    from_column?: string;
  }>;
  addresses_questions: string[];
  is_master_data: boolean;
  is_transactional: boolean;
  is_analytical: boolean;
}

/**
 * Semantic relationship in KG-first schema
 */
export interface KGFirstRelationship {
  relationship_id: string;
  relationship_type: string;
  source_concept: string;
  target_concept: string;
  kg_area_id: string;
  cardinality: string;
  description: string;
  derived_from_fk: boolean;
  source_table?: string;
  source_column?: string;
  target_table?: string;
  target_column?: string;
  business_process?: string;
}

/**
 * GDS recommendation for KG-First schema
 */
export interface KGFirstGDSRecommendation {
  algorithm_name: string;
  algorithm_category: 'centrality' | 'community' | 'pathfinding' | 'similarity' | 'embedding';
  use_case: string;
  applicable_nodes: string[];
  applicable_relationships: string[];
  business_value: string;
  example_query: string;
  priority: 'high' | 'medium' | 'low';
  complexity: 'low' | 'medium' | 'high';
}

/**
 * Expansion idea for KG-First schema
 */
export interface KGFirstExpansionIdea {
  title: string;
  description: string;
  new_entities: string[];
  new_relationships: string[];
  kg_area: string;
  business_benefit: string;
  complexity: 'low' | 'medium' | 'high';
  priority: 'high' | 'medium' | 'low';
}

/**
 * Complete KG-first schema response
 */
export interface KGFirstSchemaResponse {
  case_id: string;
  schema_id: string;
  generated_at: string;
  concepts: KGFirstConcept[];
  relationships: KGFirstRelationship[];
  kg_areas_used: string[];
  questions_addressed: Array<{
    question_id: string;
    text: string;
    covered: boolean;
  }>;
  question_coverage_score: number;
  domain_coverage_score: number;
  mermaid_diagram: string;
  cypher_ddl: string;
  gds_recommendations?: KGFirstGDSRecommendation[];
  expansion_ideas?: KGFirstExpansionIdea[];
  llm_provider?: string;
  tokens_used: number;
}

/**
 * Generate KG-first schema using concept-first methodology
 *
 * Key differences from standard schema generation:
 * - CONCEPT-FIRST: Uses KG Area taxonomy concepts, not 1:1 table mapping
 * - QUESTION-DRIVEN: Strategic questions DRIVE the schema design
 * - SEMANTIC RELATIONSHIPS: Business process relationships (Order-to-Cash, etc.)
 * - MANY-TO-ONE: Multiple tables can map to one concept
 *
 * This operation can take several minutes as it uses LLM analysis.
 */
export const generateKGFirstSchema = async (
  caseId: string,
  llmProvider?: string
): Promise<KGFirstSchemaResponse> => {
  const params = new URLSearchParams();
  if (llmProvider) params.append('llm_provider', llmProvider);

  const response = await kgdClient.post(
    `/${caseId}/schema/kg-first${params.toString() ? '?' + params.toString() : ''}`,
    {},
    {
      timeout: 600000, // 10 minutes for AI analysis with Ollama
    }
  );
  return response.data;
};

/**
 * Get existing KG-first schema for a case
 */
export const getKGFirstSchema = async (
  caseId: string
): Promise<KGFirstSchemaResponse> => {
  const response = await kgdClient.get(
    `/${caseId}/schema/kg-first`,
    {
      timeout: 600000, // May need to generate if not cached
    }
  );
  return response.data;
};

/**
 * Schema edit payload for updating KG-First schema
 */
export interface KGFirstSchemaEditPayload {
  /** Nodes to add (new local nodes) */
  added_nodes?: Array<{
    node_label: string;
    category: string;
    description: string;
    properties: Array<{
      name: string;
      type: string;
      is_primary_key: boolean;
    }>;
  }>;
  /** Node IDs to delete */
  deleted_node_ids?: string[];
  /** Nodes to update (edited properties) */
  updated_nodes?: Array<{
    node_id: string;
    category?: string;
    description?: string;
    properties?: Array<{
      name: string;
      type: string;
      is_primary_key: boolean;
    }>;
  }>;
  /** Relationships to add */
  added_relationships?: Array<{
    relationship_type: string;
    source_node: string;
    target_node: string;
    cardinality: string;
    description: string;
  }>;
  /** Relationship IDs to delete */
  deleted_relationship_ids?: string[];
  /** Relationships to update */
  updated_relationships?: Array<{
    relationship_id: string;
    relationship_type?: string;
    cardinality?: string;
    description?: string;
  }>;
}

/**
 * Update KG-First schema with user edits
 *
 * Sends local changes (added/deleted/updated nodes and relationships) to the backend
 * for persistence in Neo4j.
 */
export const updateKGFirstSchema = async (
  caseId: string,
  edits: KGFirstSchemaEditPayload
): Promise<{
  success: boolean;
  case_id: string;
  schema_id: string;
  changes_applied: {
    nodes_added: number;
    nodes_deleted: number;
    nodes_updated: number;
    relationships_added: number;
    relationships_deleted: number;
    relationships_updated: number;
  };
  updated_at: string;
}> => {
  const response = await kgdClient.patch(
    `/${caseId}/schema/kg-first`,
    edits,
    {
      timeout: 60000, // 1 minute for save
    }
  );
  return response.data;
};

// ============================================================================
// KG-First Concept Recommendations (Phase 1 Alternative)
// ============================================================================

/**
 * KG-First concept recommendation structure
 */
export interface KGFirstConceptRecommendation {
  concept_id: string;
  concept_name: string;
  kg_area_id: string;
  kg_area_name: string;
  kg_area_type: string;
  description: string;
  relevance: 'high' | 'medium' | 'low';
  questions_addressed: string[];
  is_master_data: boolean;
  is_transactional: boolean;
  is_analytical: boolean;
  key_relationships: string[];
  typical_properties: Array<{ name: string; type: string; description: string }>;
}

/**
 * Category of recommendations
 */
export interface KGFirstRecommendationCategory {
  title: string;
  description: string;
  concepts: KGFirstConceptRecommendation[];
}

/**
 * KG Area identified from strategic questions
 */
export interface IdentifiedKGArea {
  area_id: string;
  area_name: string;
  relevance: string;
  questions_addressed: string[];
}

/**
 * Complete KG-First concept recommendations response
 */
export interface KGFirstConceptRecommendationsResponse {
  case_id: string;
  generated_at: string;
  method: string;
  llm_provider?: string;
  tokens_used?: number;
  kg_areas_identified: IdentifiedKGArea[];
  recommendations: {
    must_have: KGFirstRecommendationCategory;
    should_have: KGFirstRecommendationCategory;
    could_have: KGFirstRecommendationCategory;
  };
  summary: {
    total_concepts: number;
    must_have_count: number;
    should_have_count: number;
    could_have_count: number;
    kg_areas_count: number;
  };
  strategic_questions: Array<{ question_id: string; text: string }>;
  error?: string;
}

/**
 * Generate KG-First concept recommendations for Phase 1.
 *
 * This is a KG-First alternative to getDigitalTwinRecommendations that
 * doesn't require Neo4j tables to exist. It generates conceptual entity
 * recommendations based on:
 * 1. Strategic questions → Required KG Areas
 * 2. KG Areas → Conceptual entities from taxonomy
 */
export const getKGFirstConceptRecommendations = async (
  caseId: string,
  llmProvider?: string
): Promise<KGFirstConceptRecommendationsResponse> => {
  const params = new URLSearchParams();
  if (llmProvider) {
    params.append('llm_provider', llmProvider);
  }

  const response = await kgdClient.post(
    `/${caseId}/kg-first/recommend-concepts${params.toString() ? '?' + params.toString() : ''}`,
    {},
    {
      timeout: 300000, // 5 minutes for AI analysis
    }
  );
  return response.data;
};

/**
 * Save KG-First concept recommendations to Neo4j.
 *
 * Call this when user clicks "Next" in Phase 1 to persist the recommendations.
 */
export const saveKGFirstConceptRecommendations = async (
  caseId: string,
  recommendations: KGFirstConceptRecommendationsResponse
): Promise<{
  success: boolean;
  case_id: string;
  recommendation_id: string;
  concepts_saved: number;
  summary: {
    total_concepts: number;
    must_have_count: number;
    should_have_count: number;
    could_have_count: number;
    kg_areas_count: number;
  };
}> => {
  const response = await kgdClient.post(
    `/${caseId}/kg-first/concept-recommendations/save`,
    recommendations,
    {
      timeout: 60000, // 1 minute for save
    }
  );
  return response.data;
};

/**
 * Get saved KG-First concept recommendations for a case.
 *
 * Call this when loading Phase 1 to check if recommendations already exist.
 * Returns null if no saved recommendations exist.
 */
export const getSavedKGFirstConceptRecommendations = async (
  caseId: string
): Promise<KGFirstConceptRecommendationsResponse | null> => {
  try {
    const response = await kgdClient.get(
      `/${caseId}/kg-first/concept-recommendations`,
      {
        timeout: 30000, // 30 seconds for get
      }
    );
    return response.data;
  } catch (error: any) {
    // Return null if no recommendations found (404 or null response)
    if (error.response?.status === 404 || error.response?.data === null) {
      return null;
    }
    throw error;
  }
};

/**
 * Delete saved KG-First concept recommendations for a case.
 *
 * Call this when user wants to clear recommendations and start fresh.
 */
export const deleteKGFirstConceptRecommendations = async (
  caseId: string
): Promise<{
  success: boolean;
  case_id: string;
  concepts_deleted: number;
}> => {
  const response = await kgdClient.delete(
    `/${caseId}/kg-first/concept-recommendations`,
    {
      timeout: 30000, // 30 seconds for delete
    }
  );
  return response.data;
};

// ============================================================================
// Export all functions
// ============================================================================

export const knowledgeGraphDesignApi = {
  // Phase 1: Schema Exploration
  getModulesForSystem,
  getTablesForModule,
  getColumnsForTable,
  suggestTablesForQuestions,
  selectTablesForCase,
  deselectTablesFromCase,
  getTableSuggestions,
  updateSuggestionStatus,

  // Phase 2: Relationship Discovery
  discoverRelationships,
  getDiscoveredRelationships,
  getPendingRelationships,
  approveRelationship,
  rejectRelationship,
  bulkApproveRelationships,

  // Phase 3: Schema Generation & Preview
  generateAISchema,
  generateQuickSchema,
  getSavedSchema,
  saveQuickSchema,
  generateSchemaPreview,
  getSchemaVisualization,
  getCoverageAnalysis,
  getAnswerableQuestions,
  getGDSRecommendations,
  getExpansionPreview,

  // Digital Twin Recommendations
  getDigitalTwinRecommendations,
  getSelectedSystemsForCase,

  // Enterprise Systems Library
  getAvailableEnterpriseSystems,
  recommendSystemsForCase,

  // Schema Completion (Analysis → Design Phase)
  completeSchemaDesign,
  getCompletedSchema,

  // KG-First Schema Generation (Advanced Mode)
  generateKGFirstSchema,
  getKGFirstSchema,
  updateKGFirstSchema,

  // KG-First Concept Recommendations (Phase 1 Alternative)
  getKGFirstConceptRecommendations,
  saveKGFirstConceptRecommendations,
  getSavedKGFirstConceptRecommendations,
  deleteKGFirstConceptRecommendations,
};

export default knowledgeGraphDesignApi;
